// agent-rows/rows.ts — the canonical shapes of the `/api/agent/rows/:table`
// rows, and the pure functions that read them.
//
// `hooks.ts` owns the cache key. This module owns the SHAPE half of that pair.
// It holds no React and no transport, so each projection is a pure function
// that a unit test can call (see `rows.test.ts`).
//
// The endpoint gives a loose row: `{ id }` plus a catchall of unknown values
// (`packages/contract/src/contracts/agent.ts` → `listAgentRows`). The server
// selects the Drizzle table, so each value arrives under the TS property name
// of its column, which is camelCase. A reader that asked for `source_quote`
// therefore read `undefined`, and the review panel showed no source quote, no
// compliance flag and no due description. Each function below reads the
// camelCase name, field by field, and gives a defined value for every field.
// That is why no projection uses a blanket cast.

import type { ArtefactSummaryRow } from '@septima/contract';
import type {
    BaseDraft,
    BaseDraftVariant,
    ComposerBriefShape,
    ComposerCitation,
    ComposerHandoffStepShape,
    ComposerSourceInventoryShape,
} from '@/data/types';

/** The lifecycle every artefact row carries (ADR-0010). */
export type AgentRowReviewStatus = 'pending' | 'approved' | 'rejected' | 'revision_requested';

/** One row as the endpoint gives it. This is what the cache stores. */
export type AgentRowRecord = { id: string } & Record<string, unknown>;

// -------- Field readers --------

/** A string field, or '' when the row does not carry one. */
function str(row: Record<string, unknown>, key: string): string {
    const v = row[key];
    return typeof v === 'string' ? v : '';
}

/** A string field, or null when the row does not carry one. */
function optStr(row: Record<string, unknown>, key: string): string | null {
    const v = row[key];
    return typeof v === 'string' ? v : null;
}

/** A number field, or `fallback` when the row does not carry one. */
function num(row: Record<string, unknown>, key: string, fallback = 0): number {
    const v = row[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** A number field, or null when the row does not carry one. */
function optNum(row: Record<string, unknown>, key: string): number | null {
    const v = row[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** A boolean field, or false when the row does not carry one. */
function bool(row: Record<string, unknown>, key: string): boolean {
    return row[key] === true;
}

/** A string-array field, with every non-string member dropped. */
function strArray(row: Record<string, unknown>, key: string): string[] {
    const v = row[key];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/** The members of an array field, each as a loose record. */
function objArray(row: Record<string, unknown>, key: string): Record<string, unknown>[] {
    const v = row[key];
    if (!Array.isArray(v)) return [];
    return v.filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null);
}

/** The review lifecycle, or null for a value the lifecycle does not name. */
export function readReviewStatus(value: unknown): AgentRowReviewStatus | null {
    return value === 'pending' ||
        value === 'approved' ||
        value === 'rejected' ||
        value === 'revision_requested'
        ? value
        : null;
}

// -------- Row shapes --------

/** Fields every ATLAS post-call artefact carries. */
interface CallArtefactBase {
    id: string;
    callId: string;
    clientId: string | null;
    reviewStatus: AgentRowReviewStatus | null;
}

/** A `call_extracted_facts` row: one ATLAS insight from a call. */
export interface CallFactRow extends CallArtefactBase {
    category: string;
    text: string;
    sourceQuote: string;
    sourceTimestamp: string;
    sourceLineId: string | null;
    /** Null when ATLAS stated no confidence. */
    confidence: 'high' | 'medium' | 'low' | null;
    complianceRelevant: boolean;
    complianceReason: string | null;
}

/** A `call_action_items` row: one action ATLAS heard on a call. */
export interface CallActionItemRow extends CallArtefactBase {
    title: string;
    owner: string;
    dueDescription: string;
    note: string | null;
    sourceLineId: string | null;
}

/** A `call_kb_notes` row: one knowledge-base note from a call. */
export interface CallKbNoteRow extends CallArtefactBase {
    title: string;
    body: string;
    tags: string[];
    reviewedAt: string | null;
}

/** One tonal variant of a monthly narrative. */
export interface MonthlyNarrativeVariant {
    tone: string;
    performanceSummary: string;
    narrativeParagraph: string;
}

/** A `wealth_monthly_narrative_drafts` row. */
export interface MonthlyNarrativeDraftRow {
    id: string;
    clientId: string;
    period: string;
    selectedVariantIndex: number;
    performanceSummary: string;
    narrativeParagraph: string;
    personalContextUsed: string[];
    variants: MonthlyNarrativeVariant[];
    sourceSnapshotId: string;
    previousPeriodNarrativeId: string | null;
    kbNotesCited: string[];
    reviewStatus: AgentRowReviewStatus | null;
    rejectionReason: string | null;
}

/** A `wealth_portfolio_performance_snapshots` row. */
export interface PortfolioPerformanceSnapshotRow {
    id: string;
    clientId: string;
    period: string;
    closingValue: number;
    periodReturnPct: number;
    benchmarkReturnPct: number | null;
}

/** A `coac_action_drafts` row, as the review body reads it. */
export interface CoacActionDraftRow {
    id: string;
    reviewStatus: AgentRowReviewStatus | null;
    sourceReferenceId: string;
    narrative: string | null;
}

// -------- Projections --------

/** The confidence band, or null for anything ATLAS did not name. */
function readConfidence(value: unknown): CallFactRow['confidence'] {
    return value === 'high' || value === 'medium' || value === 'low' ? value : null;
}

export function toCallFact(row: AgentRowRecord): CallFactRow {
    return {
        id: row.id,
        callId: str(row, 'callId'),
        clientId: optStr(row, 'clientId'),
        reviewStatus: readReviewStatus(row.reviewStatus),
        category: str(row, 'category'),
        text: str(row, 'text'),
        sourceQuote: str(row, 'sourceQuote'),
        sourceTimestamp: str(row, 'sourceTimestamp'),
        sourceLineId: optStr(row, 'sourceLineId'),
        confidence: readConfidence(row.confidence),
        complianceRelevant: bool(row, 'complianceRelevant'),
        complianceReason: optStr(row, 'complianceReason'),
    };
}

export function toCallActionItem(row: AgentRowRecord): CallActionItemRow {
    return {
        id: row.id,
        callId: str(row, 'callId'),
        clientId: optStr(row, 'clientId'),
        reviewStatus: readReviewStatus(row.reviewStatus),
        title: str(row, 'title'),
        owner: str(row, 'owner'),
        dueDescription: str(row, 'dueDescription'),
        note: optStr(row, 'note'),
        sourceLineId: optStr(row, 'sourceLineId'),
    };
}

export function toCallKbNote(row: AgentRowRecord): CallKbNoteRow {
    return {
        id: row.id,
        callId: str(row, 'callId'),
        clientId: optStr(row, 'clientId'),
        reviewStatus: readReviewStatus(row.reviewStatus),
        title: str(row, 'title'),
        body: str(row, 'body'),
        tags: strArray(row, 'tags'),
        reviewedAt: optStr(row, 'reviewedAt'),
    };
}

export function toMonthlyNarrativeDraft(row: AgentRowRecord): MonthlyNarrativeDraftRow {
    return {
        id: row.id,
        clientId: str(row, 'clientId'),
        period: str(row, 'period'),
        selectedVariantIndex: num(row, 'selectedVariantIndex'),
        performanceSummary: str(row, 'performanceSummary'),
        narrativeParagraph: str(row, 'narrativeParagraph'),
        personalContextUsed: strArray(row, 'personalContextUsed'),
        variants: objArray(row, 'variants').map((v) => ({
            tone: str(v, 'tone'),
            performanceSummary: str(v, 'performanceSummary'),
            narrativeParagraph: str(v, 'narrativeParagraph'),
        })),
        sourceSnapshotId: str(row, 'sourceSnapshotId'),
        previousPeriodNarrativeId: optStr(row, 'previousPeriodNarrativeId'),
        kbNotesCited: strArray(row, 'kbNotesCited'),
        reviewStatus: readReviewStatus(row.reviewStatus),
        rejectionReason: optStr(row, 'rejectionReason'),
    };
}

export function toPerformanceSnapshot(row: AgentRowRecord): PortfolioPerformanceSnapshotRow {
    return {
        id: row.id,
        clientId: str(row, 'clientId'),
        period: str(row, 'period'),
        closingValue: num(row, 'closingValue'),
        periodReturnPct: num(row, 'periodReturnPct'),
        benchmarkReturnPct: optNum(row, 'benchmarkReturnPct'),
    };
}

export function toCoacActionDraft(row: AgentRowRecord): CoacActionDraftRow {
    return {
        id: row.id,
        reviewStatus: readReviewStatus(row.reviewStatus),
        sourceReferenceId: str(row, 'sourceReferenceId'),
        narrative: optStr(row, 'narrative'),
    };
}

// -------- Composer draft --------
//
// Every named composer (SCRIVENER, HERALD, EMISSARY, CHARTER, HAVEN, FORUM,
// ARBITER) writes a `*Draft` row with the same envelope, which
// `apps/web/src/data/types.ts` names `BaseDraft`. The workbench renders the
// whole envelope, so the reader below fills each block. An absent block
// becomes an empty block, never `undefined`, so the workbench cannot fault on
// a row that an older agent version wrote.

function toCitation(row: Record<string, unknown>): ComposerCitation {
    return {
        marker: str(row, 'marker'),
        sourceType: str(row, 'sourceType'),
        sourceId: str(row, 'sourceId'),
        claim: str(row, 'claim'),
    };
}

function toCitations(row: Record<string, unknown>, key: string): ComposerCitation[] {
    return objArray(row, key).map(toCitation);
}

function toBrief(value: unknown): ComposerBriefShape {
    const b = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
    return {
        what: str(b, 'what'),
        forWhom: str(b, 'forWhom'),
        byWhen: str(b, 'byWhen'),
        why: str(b, 'why'),
    };
}

function toSourceInventory(value: unknown): ComposerSourceInventoryShape {
    const s = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
    return {
        sources: objArray(s, 'sources').map((item) => ({
            sourceType: str(item, 'sourceType'),
            sourceId: str(item, 'sourceId'),
            label: str(item, 'label'),
            isStale: bool(item, 'isStale'),
            stalenessNote: optStr(item, 'stalenessNote'),
        })),
        staleMarkers: strArray(s, 'staleMarkers'),
    };
}

function toHandoffStep(row: Record<string, unknown>): ComposerHandoffStepShape {
    return {
        role: str(row, 'role'),
        status: row.status === 'done' ? 'done' : 'pending',
        completedAt: optStr(row, 'completedAt'),
    };
}

function toDraftVariant(row: Record<string, unknown>): BaseDraftVariant {
    return {
        kind: str(row, 'kind'),
        body: str(row, 'body'),
        citations: toCitations(row, 'citations'),
    };
}

export function toComposerDraft(row: AgentRowRecord): BaseDraft {
    const traceRef = row.traceRef;
    return {
        // Each of the seven `*Draft` tables ADDS fields over the shared
        // envelope, and each per-agent wrapper renders its own set: SCRIVENER
        // reads `recommendations` and `riskWarnings`, CHARTER reads `kind`,
        // and so on. Those additions are optional on the wrapper's own type,
        // so the compiler cannot catch a projection that drops them. Carry the
        // whole row through, then fill each SHARED block from it below.
        ...row,
        id: row.id,
        clientId: optStr(row, 'clientId') ?? undefined,
        brief: toBrief(row.brief),
        sourceInventory: toSourceInventory(row.sourceInventory),
        compositionPlan: strArray(row, 'compositionPlan'),
        variants: objArray(row, 'variants').map(toDraftVariant),
        selectedVariantIndex: num(row, 'selectedVariantIndex'),
        handoffChain: objArray(row, 'handoffChain').map(toHandoffStep),
        citations: toCitations(row, 'citations'),
        counterfactualAnchor: optStr(row, 'counterfactualAnchor'),
        redlineVsPrior: optStr(row, 'redlineVsPrior'),
        traceRef:
            typeof traceRef === 'object' && traceRef !== null
                ? (traceRef as Record<string, unknown>)
                : null,
        reviewStatus: readReviewStatus(row.reviewStatus),
        reviewedBy: optStr(row, 'reviewedBy'),
        reviewedAt: optStr(row, 'reviewedAt'),
        rejectionReason: optStr(row, 'rejectionReason'),
        revisionReason: optStr(row, 'revisionReason'),
        materialisedLetterId: optStr(row, 'materialisedLetterId'),
    };
}

// -------- Artefact summary --------
//
// The review queue reads only the small set of content scalars that build a
// one-line summary. It is a projection of the same stored row, not a second
// key.

export function toArtefactSummary(row: AgentRowRecord): ArtefactSummaryRow {
    return {
        id: row.id,
        reviewStatus: readReviewStatus(row.reviewStatus),
        clientId: optStr(row, 'clientId'),
        clientName: optStr(row, 'clientName'),
        summary: optStr(row, 'summary'),
        subject: optStr(row, 'subject'),
        headline: optStr(row, 'headline'),
        claim: optStr(row, 'claim'),
        ruleStatement: optStr(row, 'ruleStatement'),
        signalSummary: optStr(row, 'signalSummary'),
        variants: objArray(row, 'variants').map((v) => ({ body: optStr(v, 'body') })),
    };
}
